import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { UserRole } from '@/lib/roles';

// ── The current signed-in user ──────────────────────────────────────────────
//
// One source of truth for "who am I", replacing twelve near-identical queries
// that each selected different columns under the same react-query key and so
// overwrote each other in the shared cache. That collision is what made the
// sidebar name revert to "? User" after an idle refetch.
//
// Every consumer now reads this one query, so there is a single fetch and one
// coherent shape. The shape is the superset of what all twelve needed.

export type AccessDenial = 'unprovisioned' | 'revoked';

export interface CurrentUser {
  /**
   * The user's role, or null. Null is never a fabricated fallback: it means
   * signed out, not provisioned, or deactivated. `denied` says which.
   */
  role: UserRole | null;
  /** Why `role` is null despite a session; null when access is fine or absent. */
  denied: AccessDenial | null;
  /** technicians.id. Exposed as both names — different call sites read each. */
  technicianId: string | null;
  id: string | null;
  name: string | null;
  initials: string | null;
  alias: string | null;
  email: string | null;
  locationIds: string[];
  /** Individually granted the Video Playlists page. See canAccessVideoPlaylists. */
  canManagePlaylists: boolean;
}

/** The columns the query reads. */
interface TechnicianRow {
  id: string;
  role: string | null;
  name: string | null;
  initials: string | null;
  alias: string | null;
  location_ids: string[] | null;
  is_active: boolean | null;
  can_manage_playlists: boolean | null;
}

const SIGNED_OUT: CurrentUser = {
  role: null,
  denied: null,
  technicianId: null,
  id: null,
  name: null,
  initials: null,
  alias: null,
  email: null,
  locationIds: [],
  canManagePlaylists: false,
};

/**
 * Map a technicians row (or its absence) to the CurrentUser shape.
 *
 * Pure and exported for testing. The caller has already established that a
 * session exists; this decides what that session is allowed to be.
 *
 * Fail closed: no row is 'unprovisioned', an inactive row is 'revoked', and
 * both yield a null role rather than a default. An account is never granted a
 * role it does not hold because of a missing or deactivated record.
 */
export function currentUserFromRow(
  row: TechnicianRow | null,
  email: string | null,
): CurrentUser {
  if (!row) return { ...SIGNED_OUT, email, denied: 'unprovisioned' };
  if (!row.is_active) return { ...SIGNED_OUT, email, denied: 'revoked' };

  return {
    // technicians.role is NOT NULL DEFAULT 'technician', so the fallback is a
    // belt-and-braces least-privilege default rather than a live branch.
    role: (row.role || 'technician') as UserRole,
    denied: null,
    technicianId: row.id,
    id: row.id,
    name: row.name ?? null,
    // `||` not `??`: an empty initials column falls back to the name, matching
    // the original behaviour this replaced.
    initials: row.initials || row.name?.charAt(0)?.toUpperCase() || '?',
    alias: row.alias ?? null,
    email,
    locationIds: row.location_ids ?? [],
    canManagePlaylists: row.can_manage_playlists === true,
  };
}

/** Fetch and resolve the current user. Throws on a failed lookup so the query retries. */
export async function fetchCurrentUser(): Promise<CurrentUser> {
  const { data: { session } } = await supabase.auth.getSession();
  const sessionUser = session?.user;
  if (!sessionUser) return SIGNED_OUT;

  const { data, error } = await supabase
    .from('technicians')
    .select('id, role, name, initials, alias, location_ids, is_active, can_manage_playlists')
    .eq('user_id', sessionUser.id)
    .maybeSingle();

  // A failed lookup is not "no record". Throwing lets react-query retry and
  // hold the last good value in the meantime, so a network blip does not read
  // as an unprovisioned account or wipe the displayed identity.
  if (error) throw error;

  return currentUserFromRow(data as TechnicianRow | null, sessionUser.email ?? null);
}

/**
 * The current user, cached and shared across every consumer.
 *
 * @param options.enabled  gate the fetch. App passes `!!session` so the query
 *   waits for auth to resolve; everything else renders only when authenticated
 *   and can leave it enabled.
 */
export function useCurrentUser(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['current-user'],
    queryFn: fetchCurrentUser,
    enabled: options.enabled ?? true,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    retry: 2,
  });
}
